export const STORAGE_KEYS = {
  entries: "weight-tracker-entries",
  vitals: "weight-tracker-vitals",
  forecast: "weight-tracker-forecast",
  sprints: "weight-tracker-sprints",
  roadmap: "weight-tracker-roadmap",
};

export function createMemoryStorage() {
  const mem = new Map<string, string>();
  return {
    async get(key: string) {
      return { value: mem.has(key) ? mem.get(key)! : null };
    },
    async set(key: string, value: string) {
      mem.set(key, value);
      return true;
    },
    async delete(key: string) {
      mem.delete(key);
      return true;
    },
    kind: "memory",
  };
}

export function canUseLocalStorage() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const k = "__wtp_test__";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

export function createLocalStorageAdapter() {
  return {
    async get(key: string) {
      try {
        const v = window.localStorage.getItem(key);
        return { value: v === null ? null : v };
      } catch {
        return { value: null };
      }
    },
    async set(key: string, value: string) {
      window.localStorage.setItem(key, value);
      return true;
    },
    async delete(key: string) {
      window.localStorage.removeItem(key);
      return true;
    },
    kind: "localStorage",
  };
}

export function createWindowStorageAdapter(winStorage: { get: (k: string) => Promise<unknown>; set: (k: string, v: string | null) => Promise<unknown>; delete?: (k: string) => Promise<unknown> }) {
  return {
    async get(key: string) {
      const res = await winStorage.get(key);
      if (res && typeof res === "object" && "value" in (res as Record<string, unknown>)) return { value: (res as { value: unknown }).value };
      return { value: res ?? null };
    },
    async set(key: string, value: string) {
      return winStorage.set(key, value);
    },
    async delete(key: string) {
      if (typeof winStorage.delete === "function") return winStorage.delete(key);
      return winStorage.set(key, null);
    },
    kind: "window.storage",
  };
}

export function getStorage() {
  if (typeof window !== "undefined") {
    const ws = (window as Window & { storage?: { get: (k: string) => Promise<unknown>; set: (k: string, v: string | null) => Promise<unknown>; delete?: (k: string) => Promise<unknown> } }).storage;
    if (ws && typeof ws.get === "function" && typeof ws.set === "function") return createWindowStorageAdapter(ws);
    if (canUseLocalStorage()) return createLocalStorageAdapter();
  }
  return createMemoryStorage();
}
